import { EventApi, EventHoveringArg } from '@fullcalendar/react';
import {
  eachDayOfInterval,
  endOfWeek,
  isSameDay,
  isToday,
  startOfHour,
  startOfWeek,
  isSameMonth,
  isSameYear,
  add,
  subMilliseconds,
  addMilliseconds,
  addDays,
  addWeeks,
  eachWeekOfInterval,
  isAfter,
  isBefore,
} from 'date-fns';
import { toDate, utcToZonedTime, format, zonedTimeToUtc } from 'date-fns-tz';
import compareAsc from 'date-fns/compareAsc';
import { NextRouter } from 'next/router';

import { ChangeMethod } from '@/hooks/useQueryParams';
import { ScheduleEntry } from '@/resources/ScheduleEntry';
import { ThinEventSession } from '@/resources/ThinEventSession';

interface IChangeOptions {
  method?: ChangeMethod;
  shallow?: boolean;
}

interface IUrlQueryStringParams {
  day?: string;
  view?: string;
  eventId?: string;
  trackId?: string;
  timeZone?: string;
}

export const handleEventMouseEnter = (mouseEnterInfo: EventHoveringArg) => {
  mouseEnterInfo.el.classList.add('hovered');
};

export const handleEventMouseLeave = (mouseEnterInfo: EventHoveringArg) => {
  mouseEnterInfo?.el?.classList.remove('hovered');
};

export const handleEventClick = (info: EventApi, router: NextRouter, forceNewTab = false) => {
  if (info?.extendedProps?.externalLink) {
    window.open(info?.extendedProps?.externalLink, '_blank');
  } else if (info?.extendedProps?.routeUrl && info?.extendedProps?.routeAs) {
    if (forceNewTab) {
      window.open(info?.extendedProps?.routeAs, '_blank');
    } else {
      router.push(info.extendedProps.routeUrl, info.extendedProps.routeAs);
    }
  }
};

export const getNormalizedEvents = (
  events: Record<string, Array<ThinEventSession>> | [],
  eventId: string,
  timeZone: string,
  eventDefaultColors: { textColor: string; backgroundColor: string },
  withLinks = true
) => {
  if (!events || !timeZone) {
    return [];
  }

  return Object.entries(events)
    .map(([_, items]) =>
      items.map((item) => {
        if (item instanceof ThinEventSession) {
          const type = item.posterOnly ? 'posters' : 'sessions';

          return {
            ...(item.startsAt && {
              start: utcToZonedTime(item.startsAt, timeZone).toISOString(),
            }),
            ...(item.endsAt && {
              end: utcToZonedTime(item.endsAt, timeZone).toISOString(),
            }),
            color: item?.track?.backgroundColor ?? eventDefaultColors.backgroundColor,
            timeFormat: 'H(:mm)',
            title: item.name,
            textColor: item?.track?.textColor ?? eventDefaultColors.textColor,
            height: '50px',
            display: 'list-item',
            itemId: item?.id,
            type,
            externalLink: item?.url,
            routeUrl: withLinks ? `/events/[eventId]/${type}?eventSessionId=${item?.id}` : undefined,
            routeAs: withLinks ? `/events/${eventId}/${type}?eventSessionId=${item?.id}` : undefined,
            session: item,
            venue: item.venue,
          };
        }
      })
    )
    .flat();
};

export const changeActiveDay = (
  change: (query: IUrlQueryStringParams, options: IChangeOptions) => Promise<boolean>,
  days: Array<string>,
  activeDay: string,
  timeZone: string,
  adjustedTimezone: string
) => {
  if (activeDay || !days || !timeZone) {
    return;
  }

  const day = days.find((tabDays) => isToday(zonedTimeToUtc(tabDays, adjustedTimezone))) || days[0];

  change({ day }, { method: 'replace', shallow: true });
};

export const getEachDayOfInterval = (eventSessionInterval, timeZone) => {
  if (timeZone && eventSessionInterval && eventSessionInterval.length > 0) {
    const start = utcToZonedTime(eventSessionInterval[0].startsAt, timeZone);
    const end = utcToZonedTime(eventSessionInterval[1].endsAt, timeZone);

    return eachDayOfInterval({
      start,
      end,
    }).map((_, index) => {
      if (index === 0) {
        return start.toISOString();
      }
      return add(start, { days: index }).toISOString();
    });
  }

  return null;
};

export const sortScheduleItems = (a: ThinEventSession | ScheduleEntry, b: ThinEventSession | ScheduleEntry) => {
  const startDateComparison = compareAsc(new Date(a.startsAt), new Date(b.startsAt));

  if (startDateComparison !== 0) {
    return startDateComparison;
  }

  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
};

export const getTracksGroups = (eventSessions): Record<string, Array<ThinEventSession>> | [] => {
  if (!eventSessions) {
    return [];
  }

  return [...eventSessions.filter((session) => !session.track?.main)]
    .sort(sortScheduleItems)
    .reduce((timeGroupedEntries, entries) => {
      const dateAndHour = startOfHour(new Date(entries.startsAt)).toISOString();

      timeGroupedEntries[dateAndHour] = timeGroupedEntries[dateAndHour]
        ? [...timeGroupedEntries[dateAndHour], entries]
        : [entries];

      return timeGroupedEntries;
    }, {} as Record<string, Array<ThinEventSession>>);
};

export const getFirstEventByDate = (
  tracksGroups: Record<string, Array<ThinEventSession>> | [],
  activeDay: string,
  timeZone: string
): string => {
  if (!tracksGroups || !activeDay || !timeZone) {
    return null;
  }

  return Object.keys(tracksGroups)
    .sort()
    .find((sessionDate) => isSameDay(utcToZonedTime(sessionDate, timeZone), toDate(activeDay)));
};

export const getDaysButtons = (days: Array<string>, activeDay: string) => {
  if (!days) {
    return [];
  }
  const index = days.indexOf(activeDay);

  if (index === -1) {
    return [];
  }
  let indexStart;
  let indexEnd;

  if (index === 0) {
    indexStart = index;
    indexEnd = index + 3;
  } else if (index === days.length - 1 && days.length > 2) {
    indexStart = index - 2;
    indexEnd = index + 1;
  } else {
    indexStart = index - 1;
    indexEnd = index + 2;
  }

  const daysButton = days.slice(indexStart, indexEnd);

  return daysButton.reduce<Array<{ id: string; text: string; order: number }>>((acc, day) => {
    return [
      ...acc,
      {
        id: day,
        text:
          activeDay === day
            ? `${format(toDate(day), 'dd')}
        ${format(toDate(day), 'MMMM').slice(0, 3)} ${format(toDate(day), 'Y')}`
            : `${format(toDate(day), 'dd')}
        ${format(toDate(day), 'MMMM').slice(0, 3)}`,
        order: days.indexOf(day) + 1,
      },
    ];
  }, []);
};

export const getWeekInterval = (activeDay: string) => {
  if (!activeDay) {
    return '';
  }

  const currentWeekStart = startOfWeek(new Date(activeDay));
  const currentWeekEnd = endOfWeek(new Date(activeDay));

  const monthStart = !isSameMonth(currentWeekStart, currentWeekEnd)
    ? `${new Date(currentWeekStart).toLocaleString('en-GB', {
        month: 'long',
      })}`
    : '';
  const monthEnd = `${new Date(currentWeekEnd).toLocaleString('en-GB', {
    month: 'long',
  })}`;
  const yearStart = !isSameYear(currentWeekStart, currentWeekEnd)
    ? `${new Date(currentWeekStart).toLocaleString('en-GB', {
        year: 'numeric',
      })}`
    : '';
  const yearEnd = `${new Date(currentWeekEnd).toLocaleString('en-GB', {
    year: 'numeric',
  })}`;

  return `${currentWeekStart.getDate()} ${monthStart} ${yearStart}
  - ${currentWeekEnd.getDate()} ${monthEnd} ${yearEnd}`;
};

export const getZonedDayInterval = (startOfActiveDay: Date, timezoneOffset: number) => {
  if (!startOfActiveDay || timezoneOffset === undefined) {
    return {
      startAtISO: null,
      endAtISO: null,
    };
  }

  const startOfActiveDayUTC = format(startOfActiveDay, "yyyy-MM-dd'T'HH:mm:ss'.000Z'");
  const startDayZoned =
    timezoneOffset > 0
      ? subMilliseconds(toDate(startOfActiveDayUTC), Math.abs(timezoneOffset))
      : addMilliseconds(toDate(startOfActiveDayUTC), Math.abs(timezoneOffset));

  const startDayISO = startDayZoned.toISOString();
  const endDay = addDays(startDayZoned, 1);
  const endDayISO = endDay.toISOString();

  return {
    startAtISO: startDayISO,
    endAtISO: endDayISO,
  };
};

export const getZonedWeekInterval = (startOfActiveDay: Date, timezoneOffset: number) => {
  if (!startOfActiveDay || timezoneOffset === undefined) {
    return {
      startAtISO: null,
      endAtISO: null,
    };
  }

  const startWeekUTC = format(startOfWeek(toDate(startOfActiveDay)), "yyyy-MM-dd'T'HH:mm:ss'.000Z'");
  const startWeekZoned =
    timezoneOffset > 0
      ? subMilliseconds(toDate(startWeekUTC), Math.abs(timezoneOffset))
      : addMilliseconds(toDate(startWeekUTC), Math.abs(timezoneOffset));
  const startWeekISO = startWeekZoned.toISOString();
  const endWeek = addWeeks(startWeekZoned, 1);
  const endWeekISO = endWeek.toISOString();

  return {
    startAtISO: startWeekISO,
    endAtISO: endWeekISO,
  };
};

export const getTimeZoneLabel = (timeZone, timezonesList) => {
  const { timeZone: systemTimeZone } = Intl.DateTimeFormat().resolvedOptions();
  const currentTimezone = timezonesList.find((zone) => zone === timeZone);

  return currentTimezone ?? systemTimeZone;
};

export const getWeekButtons = (activeDay: string, endEventDay: string, startEventDay: string, timeZone: string) => {
  if (!activeDay) {
    return [];
  }
  const currentWeekStart = startOfWeek(new Date(activeDay));

  const allWeeks = eachWeekOfInterval({
    start: new Date(utcToZonedTime(startEventDay, timeZone)),
    end: new Date(utcToZonedTime(endEventDay, timeZone)),
  });

  const index = allWeeks.findIndex((item) => item.toString() == currentWeekStart.toString());

  if (index === -1) {
    return [];
  }
  let indexStart;
  let indexEnd;

  if (index === 0) {
    indexStart = index;
    indexEnd = index + 3;
  } else if (index === allWeeks.length - 1 && allWeeks.length > 2) {
    indexStart = index - 2;
    indexEnd = index + 1;
  } else {
    indexStart = index - 1;
    indexEnd = index + 2;
  }

  const weeksButton = allWeeks.slice(indexStart, indexEnd);

  return weeksButton.reduce<Array<{ link: string; weeks: string; currentWeek: boolean }>>((acc, week) => {
    const weekStart = startOfWeek(week);
    const weekEnd = endOfWeek(week);
    const currentWeek = weekStart.toString() === currentWeekStart.toString();

    const monthStart = !isSameMonth(weekStart, weekEnd)
      ? `${new Date(weekStart).toLocaleString('en-GB', {
          month: 'short',
        })}`
      : '';
    const monthEnd = `${new Date(weekEnd).toLocaleString('en-GB', {
      month: 'short',
    })}`;
    const yearStart = !isSameYear(weekStart, weekEnd)
      ? `${new Date(weekStart).toLocaleString('en-GB', {
          year: 'numeric',
        })}`
      : '';
    const yearEnd = `${new Date(weekEnd).toLocaleString('en-GB', {
      year: 'numeric',
    })}`;

    return [
      ...acc,
      {
        link: format(toDate(weekStart), "yyyy-MM-dd'T'HH:mm:ss'.000Z'"),
        weeks: currentWeek
          ? `${weekStart.getDate()} ${monthStart} ${yearStart}
  - ${weekEnd.getDate()} ${monthEnd} ${yearEnd}`
          : `${weekStart.getDate()} - ${weekEnd.getDate()}`,
        currentWeek,
      },
    ];
  }, []);
};

export const checkIsLive = (startsAt, endsAt, timeZone) => {
  if (!startsAt || !endsAt || !timeZone) {
    return false;
  }

  const zonedSystemTime = utcToZonedTime(Date.now(), timeZone);

  return (
    isAfter(zonedSystemTime, utcToZonedTime(startsAt, timeZone)) &&
    isBefore(zonedSystemTime, utcToZonedTime(endsAt, timeZone))
  );
};
